import { useEffect, useRef, memo, useState } from 'react'
import {
  createChart,
  IChartApi,
  ISeriesApi,
  LineData,
  CrosshairMode,
  Time,
  MouseEventParams,
  LineSeries,
} from 'lightweight-charts'
import { Button } from '@/components/ui/button'

interface OIDataPoint {
  timestamp: number
  value: number
  valueUsdt?: number
}

type OIUnit = 'coins' | 'usdt'

interface OIChartProps {
  data: OIDataPoint[]
  currentOI: number | null
  currentOIUsdt?: number | null
  symbol: string
  exchange?: string
  onCrosshairMove?: (time: Time | null, value: number | null) => void
  syncedTime?: Time | null
  height?: number
}

function formatOI(value: number): string {
  if (value >= 1_000_000_000) {
    return (value / 1_000_000_000).toFixed(3) + 'B'
  }
  if (value >= 1_000_000) {
    return (value / 1_000_000).toFixed(3) + 'M'
  }
  if (value >= 1_000) {
    return (value / 1_000).toFixed(3) + 'K'
  }
  return value.toFixed(2)
}

function OIChartComponent({
  data,
  currentOI,
  currentOIUsdt,
  symbol,
  exchange,
  onCrosshairMove,
  syncedTime,
  height = 150,
}: OIChartProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const seriesRef = useRef<ISeriesApi<'Line'> | null>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [unit, setUnit] = useState<OIUnit>('coins')
  const [hoveredData, setHoveredData] = useState<{ time: string; value: number; valueUsdt?: number } | null>(null)

  // Initialize chart
  useEffect(() => {
    if (!containerRef.current) return

    const chart = createChart(containerRef.current, {
      layout: {
        background: { color: '#1a1a2e' },
        textColor: '#d1d4dc',
      },
      grid: {
        vertLines: { color: '#2B2B43' },
        horzLines: { color: '#2B2B43' },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          width: 1,
          color: '#758696',
          style: 3,
        },
        horzLine: {
          width: 1,
          color: '#758696',
          style: 3,
        },
      },
      rightPriceScale: {
        borderColor: '#2B2B43',
        scaleMargins: {
          top: 0.1,
          bottom: 0.1,
        },
      },
      timeScale: {
        borderColor: '#2B2B43',
        timeVisible: true,
        secondsVisible: false,
      },
      handleScroll: {
        vertTouchDrag: false,
      },
    })

    const lineSeries = chart.addSeries(LineSeries, {
      color: '#00bcd4',
      lineWidth: 2,
      crosshairMarkerVisible: true,
      crosshairMarkerRadius: 4,
      priceFormat: {
        type: 'custom',
        formatter: (price: number) => formatOI(price),
      },
    })

    chartRef.current = chart
    seriesRef.current = lineSeries

    // Subscribe to crosshair move
    chart.subscribeCrosshairMove((param: MouseEventParams) => {
      if (param.time && param.point) {
        const oiValue = param.seriesData.get(lineSeries)
        if (oiValue && 'value' in oiValue) {
          const value = (oiValue as LineData).value
          
          // Update tooltip
          if (tooltipRef.current) {
            const dateStr = new Date((param.time as number) * 1000).toLocaleString()
            setHoveredData({
              time: dateStr,
              value: value,
              valueUsdt: currentOIUsdt || undefined,
            })
            
            const coordinate = param.point
            tooltipRef.current.style.left = `${coordinate.x + 15}px`
            tooltipRef.current.style.top = `${coordinate.y + 15}px`
            tooltipRef.current.style.display = 'block'
          }
          
          if (onCrosshairMove) {
            onCrosshairMove(param.time, value)
          }
        } else {
          if (tooltipRef.current) {
            tooltipRef.current.style.display = 'none'
          }
          if (onCrosshairMove) {
            onCrosshairMove(param.time, null)
          }
        }
      } else {
        if (tooltipRef.current) {
          tooltipRef.current.style.display = 'none'
        }
        setHoveredData(null)
        if (onCrosshairMove) {
          onCrosshairMove(null, null)
        }
      }
    })

    // Handle resize
    const handleResize = () => {
      if (containerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: containerRef.current.clientWidth,
        })
      }
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      chart.remove()
      chartRef.current = null
      seriesRef.current = null
    }
  }, [onCrosshairMove, currentOIUsdt])

  // Update data
  useEffect(() => {
    if (!seriesRef.current) return

    const displayValue = unit === 'usdt' ? currentOIUsdt : currentOI

    // If we have historical data, use it
    if (data.length > 0) {
      const lineData: LineData[] = data.map((d) => ({
        time: d.timestamp as Time,
        value: unit === 'usdt' ? (d.valueUsdt || d.value) : d.value,
      }))
      seriesRef.current.setData(lineData)
      chartRef.current?.timeScale().fitContent()
    } else if (displayValue !== null && displayValue !== undefined) {
      // Otherwise just show current OI as a single point
      const now = Math.floor(Date.now() / 1000)
      seriesRef.current.setData([{ time: now as Time, value: displayValue }])
    } else {
      seriesRef.current.setData([])
    }
  }, [data, currentOI, currentOIUsdt, unit])

  // Sync crosshair from other charts
  useEffect(() => {
    if (!chartRef.current || syncedTime === undefined) return

    if (syncedTime === null) {
      chartRef.current.clearCrosshairPosition()
    } else {
      const displayVal = unit === 'usdt' ? (currentOIUsdt || currentOI) : currentOI
      if (displayVal !== null && displayVal !== undefined && seriesRef.current) {
        const numValue = displayVal as number
        chartRef.current.setCrosshairPosition(numValue, syncedTime, seriesRef.current)
      }
    }
  }, [syncedTime, currentOI, unit, currentOIUsdt])

  // Display value based on selected unit
  const displayOI = unit === 'usdt' ? currentOIUsdt : currentOI
  const hasData = data.length > 0 || displayOI !== null

  return (
    <div className="relative">
      {/* Header */}
      <div className="absolute top-1.5 left-1.5 z-10 flex items-center gap-2">
        <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">
          Open Interest {exchange && <span className="text-xs text-gray-500">({exchange})</span>}
        </span>
        {displayOI !== null && displayOI !== undefined && (
          <span className="text-sm font-semibold text-cyan-400">
            {formatOI(displayOI as number)}
          </span>
        )}
        {!hasData && (
          <span className="text-sm text-red-400">No data</span>
        )}
      </div>

      {/* Unit toggle */}
      {(currentOI !== null || currentOIUsdt !== null) && (
        <div className="absolute top-1.5 right-1.5 z-10 flex gap-1">
          <Button
            size="sm"
            variant={unit === 'coins' ? 'default' : 'outline'}
            onClick={() => setUnit('coins')}
            className="h-5 px-1.5 text-[10px]"
          >
            Coins
          </Button>
          <Button
            size="sm"
            variant={unit === 'usdt' ? 'default' : 'outline'}
            onClick={() => setUnit('usdt')}
            className="h-5 px-1.5 text-[10px]"
          >
            USDT
          </Button>
        </div>
      )}
      
      {/* Chart container */}
      <div ref={containerRef} style={{ height }} />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="absolute hidden pointer-events-none bg-gray-900/95 border border-gray-700 rounded px-3 py-2 text-xs z-30"
        style={{ display: 'none' }}
      >
        {hoveredData && (
          <div className="space-y-1">
            <div className="text-gray-400">{hoveredData.time}</div>
            <div className="flex items-center gap-2">
              <span className="text-gray-400">OI (Coins):</span>
              <span className="text-cyan-400 font-medium">{formatOI(hoveredData.value)}</span>
            </div>
            {hoveredData.valueUsdt !== undefined && (
              <div className="flex items-center gap-2">
                <span className="text-gray-400">OI (USDT):</span>
                <span className="text-cyan-400 font-medium">${formatOI(hoveredData.valueUsdt)}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export const OIChart = memo(OIChartComponent)
